import { useEffect, useState, useSyncExternalStore } from 'react';
import type MiniSearch from 'minisearch';
import { allFoods, ensureCatalog, type CatalogStatus } from '@/lib/foods';
import { buildIndex, indexFood, type SearchDoc } from '@/lib/search';
import type { FoodRow } from '@/lib/db';

export interface Catalog {
  status: CatalogStatus;
  index: MiniSearch<SearchDoc> | null;
  byId: Map<string, FoodRow>;
  error: string | null;
}

/**
 * Caché a nivel de módulo: el índice se construye una vez por sesión y lo
 * comparten todas las pantallas. Reconstruirlo en cada montaje costaría medio
 * segundo cada vez que se entra en "Añadir".
 */
let cached: { index: MiniSearch<SearchDoc>; byId: Map<string, FoodRow> } | null = null;
let pending: Promise<void> | null = null;

// Cada cambio de la caché se anuncia: una pantalla ya montada tiene que
// enterarse. Sin esto, tras crear un alimento el índice quedaba a null en
// "Añadir" y toda búsqueda daba "Sin resultados" (hallazgo R8).
let version = 0;
const listeners = new Set<() => void>();

function changed(): void {
  version++;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Tira la caché entera (p. ej. al borrar la cuenta): se vuelve a cargar de Dexie. */
export function invalidateCatalogCache(): void {
  cached = null;
  pending = null;
  changed();
}

/**
 * Un alimento nuevo o cambiado entra en el catálogo en memoria al momento,
 * sin reconstruir el índice. `byId` es un Map NUEVO para que los `useMemo` que
 * dependen de él se recalculen.
 */
export function upsertCatalogFood(food: FoodRow): void {
  if (!cached) return; // Aún no se ha cargado: ya lo leerá de Dexie.
  indexFood(cached.index, food);
  const byId = new Map(cached.byId);
  if (food.deletedAt == null) byId.set(food.id, food);
  else byId.delete(food.id);
  cached = { index: cached.index, byId };
  changed();
}

export function useCatalog(): Catalog {
  const current = useSyncExternalStore(subscribe, () => version);
  const [status, setStatus] = useState<CatalogStatus>({
    state: 'cargando',
    loaded: 0,
    total: 0,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cached) return;
    let alive = true;

    pending ??= (async () => {
      await ensureCatalog((s) => {
        if (alive) setStatus(s);
      });
      const foods = await allFoods();
      cached = {
        index: buildIndex(foods),
        byId: new Map<string, FoodRow>(foods.map((f) => [f.id, f] as [string, FoodRow])),
      };
    })();

    pending
      .then(() => {
        if (alive) changed();
      })
      .catch((e: unknown) => {
        pending = null;
        if (!alive) return;
        setError(e instanceof Error ? e.message : 'No se pudo cargar el catálogo');
        setStatus({ state: 'error', loaded: 0, total: 0 });
      });

    return () => {
      alive = false;
    };
  }, [current]);

  // Con la caché en memoria, "listo" se deduce de ella, no del estado local:
  // así una pantalla montada tras una invalidación no dice "listo" sin índice.
  return {
    status: cached
      ? { state: 'listo', loaded: cached.byId.size, total: cached.byId.size }
      : status.state === 'listo'
        ? { state: 'cargando', loaded: 0, total: 0 }
        : status,
    index: cached?.index ?? null,
    byId: cached?.byId ?? new Map<string, FoodRow>(),
    error,
  };
}
